import { useMutation } from "@apollo/client/react";
import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconGripVertical } from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { useCallback, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";

const UpdatePlatformThemeMutation = graphql(`
  mutation UpdatePlatformThemeSections(
    $platformId: ID!
    $input: PlatformThemeInput!
  ) {
    platformThemeUpdate(platformId: $platformId, input: $input) {
      id
    }
  }
`);

interface SectionConfig {
  name: string;
  visible: boolean;
  order: number;
}

interface SectionManagerProps {
  platformId: string;
  templateContent: string;
  currentSections?: SectionConfig[];
}

function parseSectionsFromTemplate(content: string): string[] {
  const regex = /\{%\s*section\s+name:\s*"([^"]+)"\s*%\}/g;
  const sections: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    if (!sections.includes(match[1])) {
      sections.push(match[1]);
    }
  }
  return sections;
}

function buildInitialSections(
  templateSectionNames: string[],
  currentSections: SectionConfig[],
): SectionConfig[] {
  const existingByName = new Map(currentSections.map((s) => [s.name, s]));

  const merged = templateSectionNames.map((name, index) => {
    const existing = existingByName.get(name);
    if (existing) {
      return { ...existing };
    }
    return { name, visible: true, order: index };
  });

  return merged.sort((a, b) => a.order - b.order);
}

function formatSectionLabel(name: string): string {
  return name
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function SectionManager({
  platformId,
  templateContent,
  currentSections = [],
}: SectionManagerProps) {
  const templateSectionNames = useMemo(
    () => parseSectionsFromTemplate(templateContent),
    [templateContent],
  );

  const [sections, setSections] = useState<SectionConfig[]>(() =>
    buildInitialSections(templateSectionNames, currentSections),
  );

  const [updateTheme, { loading: saving }] = useMutation<any>(
    UpdatePlatformThemeMutation,
  );

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    setSections((prev) => {
      const reordered = [...prev];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(destinationIndex, 0, moved);
      return reordered.map((section, index) => ({
        ...section,
        order: index,
      }));
    });
  }, []);

  const handleToggleVisibility = useCallback((name: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.name === name
          ? { ...section, visible: !section.visible }
          : section,
      ),
    );
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const result = await updateTheme({
        variables: {
          platformId,
          input: {
            sections: sections.map((section) => ({
              name: section.name,
              visible: section.visible,
              order: section.order,
            })),
          },
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Sections updated",
        message: "Section order and visibility have been saved",
        color: "green",
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: "Failed to save sections",
        message:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        color: "red",
        autoClose: 5000,
      });
    }
  }, [platformId, sections, updateTheme]);

  if (templateSectionNames.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed">
          No sections found in this template. Add sections using the{" "}
          <Text span ff="monospace">
            {'{% section name:"..." %}'}
          </Text>{" "}
          pattern.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Homepage Sections</Title>
        <Button onClick={handleSave} loading={saving} size="sm">
          Save Order
        </Button>
      </Group>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections-list">
          {(provided) => (
            <Stack
              gap="xs"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {sections.map((section, index) => (
                <Draggable
                  key={section.name}
                  draggableId={section.name}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      p="sm"
                      withBorder
                      style={(theme) => ({
                        backgroundColor: snapshot.isDragging
                          ? theme.colors.dark[5]
                          : undefined,
                        opacity: section.visible ? 1 : 0.5,
                      })}
                    >
                      <Group justify="space-between">
                        <Group gap="sm">
                          <ActionIcon
                            variant="subtle"
                            style={{ cursor: "grab" }}
                            {...provided.dragHandleProps}
                          >
                            <IconGripVertical size={18} />
                          </ActionIcon>

                          <Checkbox
                            checked={section.visible}
                            onChange={() =>
                              handleToggleVisibility(section.name)
                            }
                            label={formatSectionLabel(section.name)}
                          />
                        </Group>

                        <Text size="xs" c="dimmed">
                          {section.visible ? "Visible" : "Hidden"}
                        </Text>
                      </Group>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
  );
}
